import {
  cloneElement,
  isValidElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  animate,
  motion,
  useMotionValue,
} from "motion/react";
import { projects } from "../../data/projects";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { ImageLightbox } from "./ImageLightbox";
import styles from "./ProjectImage.module.css";

export interface ProjectImageHandle {
  moveVisual: (direction: -1 | 1) => boolean;
  resetVisual: () => void;
}

interface ProjectImageProps {
  projectId: string;
  title: string;
  accent: "lime" | "blue" | "coral";
  gallery?: boolean;
  onBoundarySwipe?: (
    direction: -1 | 1,
  ) => void;
}

interface ProjectVisualSet {
  preview: ReactNode;
  gallery?: ReactNode[];
}

export const ProjectImage = forwardRef<
  ProjectImageHandle,
  ProjectImageProps
>(function ProjectImage({
  projectId,
  title,
  accent,
  gallery = false,
  onBoundarySwipe,
}, ref) {
  const reduceMotion =
    useReducedMotion();

  const [
    activeVisual,
    setActiveVisual,
  ] = useState(0);
  const [
    isExpanded,
    setIsExpanded,
  ] = useState(false);
  const [
    portraitVisuals,
    setPortraitVisuals,
  ] = useState<Set<number>>(
    () => new Set(),
  );
  const activeVisualRef = useRef(0);
  const frameRef =
    useRef<HTMLDivElement>(null);
  const galleryWasDraggedRef =
    useRef(false);
  const imageTapStartRef = useRef<{
    pointerId: number;
    index: number;
    x: number;
    y: number;
  } | null>(null);
  const galleryDragStartIndexRef =
    useRef(0);
  const galleryX = useMotionValue(0);
  const galleryWidthRef = useRef(0);
  const [galleryWidth, setGalleryWidth] =
    useState(0);
  const galleryAnimationRef = useRef<
    ReturnType<typeof animate> | null
  >(null);
  const visualSet = useMemo(
    () =>
      getProjectVisualSet(
        projectId,
        title,
      ),
    [projectId, title],
  );

  const displayedVisuals = useMemo(() => {
  if (!gallery) {
    return visualSet.preview
      ? [visualSet.preview]
      : [];
  }

  return visualSet.gallery ?? [];
}, [gallery, visualSet]);

  const lightboxImages = useMemo(
    () =>
      displayedVisuals.flatMap(
        (visual) => {
          if (
            !isValidElement(visual) ||
            visual.type !== "img"
          ) {
            return [];
          }

          const props = (
            visual as React.ReactElement<
              React.ImgHTMLAttributes<HTMLImageElement>
            >
          ).props;

          return typeof props.src ===
            "string"
            ? [
                {
                  src: props.src,
                  alt:
                    props.alt ??
                    `${title} 확대 이미지`,
                },
              ]
            : [];
        },
      ),
    [displayedVisuals, title],
  );

  const visualCount =
    displayedVisuals.length;
    

  const hasGalleryControls =
    gallery && visualCount > 1;

  const isFirstVisual =
    activeVisual === 0;

  const isLastVisual =
    activeVisual ===
    visualCount - 1;

  useEffect(() => {
    activeVisualRef.current = 0;
    setActiveVisual(0);
    setIsExpanded(false);
    setPortraitVisuals(new Set());
    galleryAnimationRef.current?.stop();
    galleryX.set(0);
  }, [projectId, gallery]);

  useEffect(() => {
    if (
      activeVisual >=
      visualCount
    ) {
      setActiveVisual(
        Math.max(
          visualCount - 1,
          0,
        ),
      );
    }
  }, [
    activeVisual,
    visualCount,
  ]);

  useEffect(() => {
    const frame = frameRef.current;

    if (!gallery || !frame) {
      return;
    }

    const updateWidth = () => {
      const width =
        frame.getBoundingClientRect().width *
        0.56;
      galleryWidthRef.current = width;
      setGalleryWidth(width);
      galleryX.set(
        -activeVisualRef.current *
          width,
      );

    };

    updateWidth();
    const resizeObserver =
      new ResizeObserver(updateWidth);
    resizeObserver.observe(frame);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    gallery,
    galleryX,
  ]);

  const snapToVisual = useCallback(
    (index: number) => {
      const nextIndex = Math.min(
        Math.max(index, 0),
        Math.max(visualCount - 1, 0),
      );
      const destination =
        -nextIndex *
        galleryWidthRef.current;

      galleryAnimationRef.current?.stop();
      activeVisualRef.current = nextIndex;
      setActiveVisual(nextIndex);

      if (reduceMotion) {
        galleryX.set(destination);
        return;
      }

      galleryAnimationRef.current = animate(
        galleryX,
        destination,
        {
          type: "spring",
          stiffness: 260,
          damping: 32,
          mass: 0.9,
        },
      );
    },
    [
      galleryX,
      reduceMotion,
      visualCount,
    ],
  );

  const moveVisual = useCallback(
    (direction: -1 | 1) => {
      if (visualCount <= 1) {
        return false;
      }

      const next =
        activeVisual + direction;

      if (
        next < 0 ||
        next >= visualCount
      ) {
        return false;
      }

      snapToVisual(next);
      return true;
    },
    [
      activeVisual,
      snapToVisual,
      visualCount,
    ],
  );

  useImperativeHandle(
    ref,
    () => ({
      moveVisual,
      resetVisual: () => {
        snapToVisual(0);
      },
    }),
    [
      moveVisual,
      snapToVisual,
    ],
  );

  return (
    <div
      ref={frameRef}
      className={`
        ${styles.visualFrame}
        ${styles[accent]}
      `}
      role="group"
      aria-label={`${title} 프로젝트 이미지`}
      onDragStart={(event) => {
        event.preventDefault();
      }}
    >
      <div
        className={styles.visual}
      >
        {gallery ? (
          <motion.div
            className={styles.carouselTrack}
            data-project-gallery=""
            drag="x"
            dragConstraints={{
              left:
                -(visualCount - 1) *
                galleryWidth,
              right: 0,
            }}
            dragElastic={0.08}
            dragMomentum={false}
            style={{ x: galleryX }}
            onPointerDown={(event) => {
              event.stopPropagation();
            }}
            onTouchStart={(event) => {
              event.stopPropagation();
            }}
            onDragStart={() => {
              galleryAnimationRef.current?.stop();
              galleryWasDraggedRef.current =
                false;
              galleryDragStartIndexRef.current =
                activeVisualRef.current;
            }}
            onDrag={(_event, info) => {
              if (
                Math.abs(info.offset.x) > 12
              ) {
                galleryWasDraggedRef.current =
                  true;
              }
            }}
            onDragEnd={(
              _event,
              info,
            ) => {
              const width =
                galleryWidthRef.current;

              if (width <= 0) {
                return;
              }

              const crossedBoundary =
                Math.abs(info.offset.x) >
                  24 ||
                Math.abs(info.velocity.x) >
                  280;

              if (
                crossedBoundary &&
                galleryDragStartIndexRef.current ===
                  0 &&
                info.offset.x > 0
              ) {
                snapToVisual(0);
                onBoundarySwipe?.(-1);

                window.setTimeout(() => {
                  galleryWasDraggedRef.current =
                    false;
                }, 0);
                return;
              }

              if (
                crossedBoundary &&
                galleryDragStartIndexRef.current ===
                  visualCount - 1 &&
                info.offset.x < 0
              ) {
                snapToVisual(
                  visualCount - 1,
                );
                onBoundarySwipe?.(1);

                window.setTimeout(() => {
                  galleryWasDraggedRef.current =
                    false;
                }, 0);
                return;
              }

              const velocityProjection =
                Math.max(
                  Math.min(
                    info.velocity.x *
                      0.05,
                    width * 0.35,
                  ),
                  -width * 0.35,
                );
              const projectedX =
                galleryX.get() +
                velocityProjection;

              snapToVisual(
                Math.round(
                  -projectedX / width,
                ),
              );

              window.setTimeout(() => {
                galleryWasDraggedRef.current =
                  false;
              }, 0);
            }}
          >
            {displayedVisuals.map(
              (visual, index) => (
                <motion.div
                  key={`${projectId}-${index}`}
                  className={
                    styles.carouselSlide
                  }
                  initial={false}
                  animate={{
                    x:
                      index *
                        galleryWidth +
                      (portraitVisuals.has(
                        activeVisual,
                      ) &&
                      index !== activeVisual
                        ? (index <
                            activeVisual
                            ? 1
                            : -1) *
                          galleryWidth *
                          0.2
                        : 0),
                    scale:
                      index === activeVisual
                        ? 1
                        : 0.42,
                    opacity:
                      Math.abs(
                        index -
                          activeVisual,
                      ) <= 1
                        ? index ===
                          activeVisual
                          ? 1
                          : 0.22
                        : 0,
                    zIndex:
                      visualCount -
                      Math.abs(
                        index -
                          activeVisual,
                      ),
                  }}
                  transition={{
                    duration:
                      reduceMotion
                        ? 0
                        : 0.45,
                    ease: [
                      0.22,
                      1,
                      0.36,
                      1,
                    ],
                  }}
                  style={{
                    pointerEvents:
                      index ===
                      activeVisual
                        ? "auto"
                        : "none",
                  }}
                  aria-hidden={
                    index !== activeVisual
                  }
                >
                  <div
                    className={
                      styles.carouselMedia
                    }
                  >
                    {isValidElement(
                      visual,
                    ) &&
                    visual.type === "img"
                      ? cloneElement(
                          visual as React.ReactElement<
                            React.ImgHTMLAttributes<HTMLImageElement>
                          >,
                          {
                            draggable:
                              false,
                            className: `
                              ${
                                (
                                  visual as React.ReactElement<
                                    React.ImgHTMLAttributes<HTMLImageElement>
                                  >
                                ).props
                                  .className ??
                                ""
                              }
                              ${
                                index ===
                                activeVisual
                                  ? styles.expandableImage
                                  : ""
                              }
                            `,
                            onPointerDown: (
                              event: React.PointerEvent<
                                HTMLImageElement
                              >,
                            ) => {
                              if (
                                !event.isPrimary ||
                                index !==
                                  activeVisualRef.current
                              ) {
                                imageTapStartRef.current =
                                  null;
                                return;
                              }

                              imageTapStartRef.current = {
                                pointerId:
                                  event.pointerId,
                                index,
                                x: event.clientX,
                                y: event.clientY,
                              };
                            },
                            onLoad: (
                              event: React.SyntheticEvent<
                                HTMLImageElement
                              >,
                            ) => {
                              (
                                visual as React.ReactElement<
                                  React.ImgHTMLAttributes<HTMLImageElement>
                                >
                              ).props.onLoad?.(
                                event,
                              );

                              const isPortrait =
                                event.currentTarget
                                  .naturalHeight >
                                event.currentTarget
                                  .naturalWidth;

                              setPortraitVisuals(
                                (current) => {
                                  if (
                                    current.has(
                                      index,
                                    ) ===
                                    isPortrait
                                  ) {
                                    return current;
                                  }

                                  const next =
                                    new Set(
                                      current,
                                    );

                                  if (
                                    isPortrait
                                  ) {
                                    next.add(
                                      index,
                                    );
                                  } else {
                                    next.delete(
                                      index,
                                    );
                                  }

                                  return next;
                                },
                              );
                            },
                            onPointerUp: (
                              event: React.PointerEvent<
                                HTMLImageElement
                              >,
                            ) => {
                              const start =
                                imageTapStartRef.current;
                              imageTapStartRef.current =
                                null;

                              if (
                                !start ||
                                start.pointerId !==
                                  event.pointerId ||
                                start.index !==
                                  activeVisualRef.current
                              ) {
                                return;
                              }

                              const distance =
                                Math.hypot(
                                  event.clientX -
                                    start.x,
                                  event.clientY -
                                    start.y,
                                );

                              if (
                                distance <= 12 &&
                                isInsideContainedImage(
                                  event,
                                )
                              ) {
                                galleryWasDraggedRef.current =
                                  false;
                                setIsExpanded(
                                  true,
                                );
                              }
                            },
                            onPointerCancel:
                              () => {
                                imageTapStartRef.current =
                                  null;
                              },
                            onClick: (
                              event: React.MouseEvent<
                                HTMLImageElement
                              >,
                            ) => {
                              event.stopPropagation();

                              if (
                                index !==
                                  activeVisual ||
                                galleryWasDraggedRef.current ||
                                !isInsideContainedImage(
                                  event,
                                )
                              ) {
                                return;
                              }

                              setIsExpanded(
                                true,
                              );
                            },
                          },
                        )
                      : visual}

                  </div>
                </motion.div>
              ),
            )}
          </motion.div>
        ) : (
          <div
            className={styles.previewSlide}
          >
            {displayedVisuals[0]}
          </div>
        )}
      </div>

      {hasGalleryControls && (
        <div
          className={
            styles.galleryControls
          }
          onPointerDown={(
            event,
          ) => {
            event.stopPropagation();
          }}
          onPointerUp={(
            event,
          ) => {
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <button
            type="button"
            aria-label="이전 프로젝트 이미지"
            disabled={
              isFirstVisual
            }
            onClick={(event) => {
              event.stopPropagation();
              moveVisual(-1);
            }}
          >
            ←
          </button>

          <strong
            className={
              styles.desktopGalleryText
            }
          >
            {String(
              activeVisual + 1,
            ).padStart(2, "0")}
            {" / "}
            {String(
              visualCount,
            ).padStart(2, "0")}
          </strong>

          <strong
            className={
              styles.mobileGalleryText
            }
          >
            {String(
              activeVisual + 1,
            ).padStart(2, "0")}
            {" / "}
            {String(
              visualCount,
            ).padStart(2, "0")}
          </strong>

          <button
            type="button"
            aria-label="다음 프로젝트 이미지"
            disabled={
              isLastVisual
            }
            onClick={(event) => {
              event.stopPropagation();
              moveVisual(1);
            }}
          >
            →
          </button>
        </div>
      )}

      <ImageLightbox
        open={isExpanded}
        images={lightboxImages}
        activeIndex={activeVisual}
        onActiveIndexChange={(
          index,
        ) => {
          if (
            index !==
            activeVisualRef.current
          ) {
            snapToVisual(index);
          }
        }}
        onClose={() => {
          setIsExpanded(false);
        }}
      />
    </div>
  );
});

function isInsideContainedImage(
  event:
    | React.MouseEvent<HTMLImageElement>
    | React.PointerEvent<HTMLImageElement>,
) {
  const image = event.currentTarget;
  const bounds =
    image.getBoundingClientRect();

  if (
    image.naturalWidth <= 0 ||
    image.naturalHeight <= 0
  ) {
    return false;
  }

  const scale = Math.min(
    bounds.width / image.naturalWidth,
    bounds.height / image.naturalHeight,
  );
  const renderedWidth =
    image.naturalWidth * scale;
  const renderedHeight =
    image.naturalHeight * scale;
  const renderedLeft =
    bounds.left +
    (bounds.width - renderedWidth) / 2;
  const renderedTop =
    bounds.top +
    (bounds.height - renderedHeight) / 2;

  return (
    event.clientX >= renderedLeft &&
    event.clientX <=
      renderedLeft + renderedWidth &&
    event.clientY >= renderedTop &&
    event.clientY <=
      renderedTop + renderedHeight
  );
}

function getProjectVisualSet(
  projectId: string,
  title: string,
): ProjectVisualSet {
  const project = projects.find(
    (entry) => entry.id === projectId,
  );

  const preview = project?.mainImage ? (
    <img
      className={styles.wideProjectImage}
      src={project.mainImage.src}
      alt={`${title} 대표 화면`}
      draggable={false}
    />
  ) : null;

  const gallery = (project?.subImages ?? []).map(
    (visual, index) => (
      <img
        key={`${projectId}-${index}`}
        className={styles.wideProjectImage}
        src={visual.src}
        alt={`${title} 화면 ${index + 1}`}
        draggable={false}
      />
    ),
  );

  return {
    preview,
    gallery,
  };
}
